import React, { useState, useEffect } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import '../App.css';
import GlobalHeader from '../components/GlobalHeader';
import DashboardToolbar from '../components/DashboardToolbar';
import LeftSidebar from '../components/LeftSidebar';
import Canvas from '../components/Canvas';
import RightSidebar from '../components/RightSidebar';
import Notification from '../components/Notification';
import NameInputModal from '../components/NameInputModal';
import TemplateSaveModal from '../components/TemplateSaveModal';
import TemplateLibraryModal from '../components/templates/TemplateLibraryModal';
import CascadeUpdateDialog from '../components/templates/CascadeUpdateDialog';
import ScheduleChangeModal from '../components/ScheduleChangeModal';
import ScheduledChangesListModal from '../components/ScheduledChangesListModal';
import { API, API_CONFIG } from '../services/api';
import { generateThumbnailBlob } from '../utils/thumbnailGenerator';

function DashboardPage({ mode = 'signature' }) {
    const location = useLocation();
    const params = useParams();
    const [grid, setGrid] = useState([]);
    const [selectedElement, setSelectedElement] = useState(null);
    const [selectedCell, setSelectedCell] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [draggedItem, setDraggedItem] = useState(null);
    const [apiStatus, setApiStatus] = useState('disconnected');
    const [currentEntityId, setCurrentEntityId] = useState(null); // Générique: signature ou template
    const [entityName, setEntityName] = useState('');
    const [notification, setNotification] = useState(null);
    const [containerWidth, setContainerWidth] = useState(600);
    const [previewMode, setPreviewMode] = useState(false);
    const [showNameModal, setShowNameModal] = useState(false);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [showCascadeDialog, setShowCascadeDialog] = useState(false);
    const [templateUsages, setTemplateUsages] = useState(null);
    const [pendingSaveData, setPendingSaveData] = useState(null);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [showScheduledChangesListModal, setShowScheduledChangesListModal] = useState(false);
    const [refreshScheduledChangesRef, setRefreshScheduledChangesRef] = useState(null);
    const [directoryMembers, setDirectoryMembers] = useState([]);
    const [previewMember, setPreviewMember] = useState(null);

    const isTemplateMode = mode === 'template';

    useEffect(() => {
        if (API_CONFIG.enabled) {
            API.checkStatus().then(status => {
                console.log('API Status:', status);
                setApiStatus(status);
            });
        }
    }, []);

    // Charger les membres de l'annuaire pour la preview
    useEffect(() => {
        const loadDirectoryMembers = async () => {
            if (!API_CONFIG.enabled || apiStatus !== 'connected') return;
            try {
                const response = await API.getDirectoryUsers();
                if (response?.data?.users) {
                    setDirectoryMembers(response.data.users);
                }
            } catch (error) {
                console.error('Failed to load directory members:', error);
            }
        };
        loadDirectoryMembers();
    }, [apiStatus]);

    // Load entity (signature or template) from URL parameter
    useEffect(() => {
        // Pour les templates, l'ID vient de useParams() (route /templates/edit/:id)
        // Pour les signatures, l'ID vient de query params (route /builder?id=X)
        let entityId = null;

        if (isTemplateMode && params.id) {
            entityId = params.id;
        } else {
            const urlParams = new URLSearchParams(location.search);
            entityId = urlParams.get('id');
        }

        if (entityId && API_CONFIG.enabled) {
            loadEntityById(entityId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, params.id, isTemplateMode]);

    // Load initial data from navigation state (e.g., from public template selection)
    useEffect(() => {
        if (location.state?.initialGrid && !currentEntityId) {
            setGrid(location.state.initialGrid);
            if (location.state.initialWidth) {
                setContainerWidth(location.state.initialWidth);
            }
            // Clear the state to prevent re-loading on refresh
            window.history.replaceState({}, document.title);
        }
    }, [location.state, currentEntityId]);

    const loadEntityById = async (id) => {
        try {
            const response = isTemplateMode
                ? await API.getTemplate(id)
                : await API.getSignature(id);

            const entity = isTemplateMode ? response.data.template : response.data.signature;

            if (entity.grid_data) {
                setGrid(entity.grid_data);
            }
            if (entity.container_width) {
                setContainerWidth(entity.container_width);
            }
            setCurrentEntityId(entity.id);
            setEntityName(entity.name || 'Sans nom');
            showNotification(
                isTemplateMode ? 'Modèle chargé!' : 'Signature chargée!',
                'success'
            );
        } catch (error) {
            console.error(`Failed to load ${isTemplateMode ? 'template' : 'signature'}:`, error);
            showNotification('Erreur lors du chargement', 'error');
        }
    };

    // Ne plus sauvegarder automatiquement dans localStorage
    // La signature est maintenant sauvegardée uniquement en BDD

    useEffect(() => {
        const handleClickOutside = (e) => {
            const canvasArea = document.querySelector('.canvas-area');
            if (canvasArea && !canvasArea.contains(e.target)) {
                return;
            }

            const isInteractive = e.target.closest('.grid-cell, .canvas-element, .grid-row, .cell-selector-btn, .cell-actions, .row-controls');

            if (!isInteractive) {
                setSelectedCell(null);
                setSelectedRow(null);
                setSelectedElement(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const showNotification = (message, type = 'info') => {
        setNotification({ message, type, id: Date.now() });
    };

    const handleSave = async () => {
        // Demander le nom si c'est une nouvelle entité
        if (!currentEntityId) {
            setShowNameModal(true);
        } else {
            // Pour les templates existants, vérifier les usages avant de sauvegarder
            if (isTemplateMode && currentEntityId) {
                await checkTemplateUsagesBeforeSave();
            } else {
                // Mise à jour directe pour les signatures
                await saveEntity(entityName);
            }
        }
    };

    const checkTemplateUsagesBeforeSave = async () => {
        try {
            // Récupérer les usages du template
            const response = await API.getTemplateUsages(currentEntityId);

            if (response && response.success) {
                setTemplateUsages(response.data);
                setPendingSaveData({ name: entityName });
                setShowCascadeDialog(true);
            }
        } catch (error) {
            console.error('Error fetching template usages:', error);
            // En cas d'erreur, procéder quand même à la sauvegarde
            await saveEntity(entityName);
        }
    };

    const handleCascadeConfirm = async () => {
        setShowCascadeDialog(false);

        if (pendingSaveData) {
            await saveEntity(pendingSaveData.name, {}, true); // true = cascade
            setPendingSaveData(null);
            setTemplateUsages(null);
        }
    };

    const handleCascadeCancel = () => {
        setShowCascadeDialog(false);
        setPendingSaveData(null);
        setTemplateUsages(null);
    };

    const saveEntity = async (name, extraData = {}, cascade = false) => {
        try {
            const data = {
                name,
                grid: grid,
                container_width: containerWidth,
                ...extraData
            };

            // Ajouter cascade si demandé (pour templates)
            if (cascade && isTemplateMode) {
                data.cascade = true;
            }

            // Ajouter des champs spécifiques aux templates si en mode template (par défaut)
            if (isTemplateMode && !extraData.category) {
                data.category = extraData.category || 'custom';
                data.is_public = extraData.isPublic !== undefined ? extraData.isPublic : false;
                data.description = extraData.description || '';
            }

            let entityId = currentEntityId;

            if (currentEntityId) {
                // Mise à jour d'une entité existante
                if (isTemplateMode) {
                    const response = await API.updateTemplate(currentEntityId, data);
                    setEntityName(name);

                    // Afficher les résultats de la cascade si disponibles
                    if (response.data.cascade) {
                        const { updated, locked, failed } = response.data.cascade;
                        showNotification(
                            `✅ Modèle mis à jour! ${updated} signature(s) mise(s) à jour${locked > 0 ? `, ${locked} verrouillée(s)` : ''}${failed > 0 ? `, ${failed} échec(s)` : ''}`,
                            'success'
                        );
                    } else {
                        showNotification('✅ Modèle mis à jour!', 'success');
                    }
                } else {
                    await API.updateSignature(currentEntityId, data);
                    setEntityName(name);
                    showNotification('✅ Signature mise à jour!', 'success');
                }
            } else {
                // Création d'une nouvelle entité
                if (isTemplateMode) {
                    const response = await API.createTemplate(data);
                    entityId = response.data.template.id;
                    setCurrentEntityId(entityId);
                    setEntityName(name);
                    showNotification('✅ Modèle créé!', 'success');
                } else {
                    const response = await API.createSignature(data);
                    entityId = response.data.signature.id;
                    setCurrentEntityId(entityId);
                    setEntityName(name);
                    showNotification('✅ Signature créée!', 'success');
                }
            }

            // Générer et uploader le thumbnail (seulement pour les signatures pour l'instant)
            if (!isTemplateMode && API_CONFIG.enabled && apiStatus === 'connected' && grid.length > 0) {
                try {
                    const thumbnailBlob = await generateThumbnailBlob(grid, containerWidth);
                    await API.uploadThumbnail(entityId, thumbnailBlob);
                    console.log('Thumbnail uploaded successfully');
                } catch (thumbnailError) {
                    console.error('Thumbnail upload error:', thumbnailError);
                    // Ne pas faire échouer la sauvegarde si le thumbnail échoue
                }

                // Synchroniser les templates insérés dans la signature
                console.log('📝 [saveEntity] About to sync template usages for signature:', entityId);
                try {
                    await syncTemplateUsages(entityId, grid);
                } catch (syncError) {
                    console.error('❌ [saveEntity] Template sync error:', syncError);
                    // Ne pas faire échouer la sauvegarde si la synchro échoue
                }
            } else {
                console.log('⚠️ [saveEntity] Skipping template sync. Conditions:', {
                    isTemplateMode,
                    apiEnabled: API_CONFIG.enabled,
                    apiStatus,
                    gridLength: grid.length
                });
            }
        } catch (error) {
            console.error('Save error:', error);
            showNotification('❌ Erreur lors de la sauvegarde', 'error');
        }
    };

    // Synchroniser les templates dans template_usages
    const syncTemplateUsages = async (signatureId, gridData) => {
        console.log('🔄 [syncTemplateUsages] Starting sync for signature:', signatureId);
        console.log('🔄 [syncTemplateUsages] Grid data:', gridData);

        let templatesFound = 0;

        // Parcourir le grid pour trouver toutes les cellules avec des templates
        for (let rowIndex = 0; rowIndex < gridData.length; rowIndex++) {
            const row = gridData[rowIndex];
            for (let cellIndex = 0; cellIndex < row.cells.length; cellIndex++) {
                const cell = row.cells[cellIndex];

                console.log(`🔍 [syncTemplateUsages] Checking cell [${rowIndex}, ${cellIndex}]:`, {
                    hasTemplateMeta: !!cell._templateMeta,
                    templateId: cell._templateMeta?.templateId,
                    cell: cell
                });

                // Si la cellule contient un template
                if (cell._templateMeta && cell._templateMeta.templateId) {
                    templatesFound++;
                    try {
                        console.log(`✅ [syncTemplateUsages] Found template ${cell._templateMeta.templateId} at [${rowIndex}, ${cellIndex}], calling API...`);

                        const result = await API.insertTemplate(signatureId, {
                            template_id: cell._templateMeta.templateId,
                            row_index: rowIndex,
                            cell_index: cellIndex
                        });

                        console.log(`✅ [syncTemplateUsages] Template ${cell._templateMeta.templateId} synced successfully:`, result);
                    } catch (error) {
                        console.error(`❌ [syncTemplateUsages] Failed to sync template at [${rowIndex}, ${cellIndex}]:`, error);
                    }
                }
            }
        }

        console.log(`🔄 [syncTemplateUsages] Sync complete. Found ${templatesFound} template(s)`);
    };

    const handleNameModalConfirm = async (name) => {
        setShowNameModal(false);
        await saveEntity(name);
    };

    const handleTemplateModalConfirm = async (templateData) => {
        setShowNameModal(false);
        const { name, description, category, isPublic } = templateData;
        await saveEntity(name, { description, category, isPublic });
    };

    const handleNameModalCancel = () => {
        setShowNameModal(false);
    };

    const handleRename = () => {
        if (currentEntityId) {
            setShowNameModal(true);
        }
    };

    const handleImageUpload = async (file) => {
        if (!API_CONFIG.enabled || apiStatus !== 'connected') {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target.result);
                reader.readAsDataURL(file);
            });
        }

        try {
            const result = await API.uploadImage(file);
            showNotification('✅ Image uploadée!', 'success');
            return result.url;
        } catch (error) {
            console.error('Upload error:', error);
            showNotification('❌ Erreur lors de l\'upload', 'error');
            return null;
        }
    };

    const handleClear = () => {
        const message = isTemplateMode
            ? 'Effacer tout le modèle et en créer un nouveau ?'
            : 'Effacer toute la signature et en créer une nouvelle ?';
        if (window.confirm(message)) {
            setGrid([]);
            setSelectedElement(null);
            setSelectedCell(null);
            setSelectedRow(null);
            setCurrentEntityId(null);
            setEntityName('');
            showNotification(`🗑️ ${isTemplateMode ? 'Nouveau modèle' : 'Nouvelle signature'}`, 'info');
        }
    };

    const handleInsertTemplate = async (templateId) => {
        try {
            // Vérifier qu'une cellule est sélectionnée
            if (!selectedCell) {
                showNotification('⚠️ Veuillez sélectionner une cellule avant d\'insérer un modèle', 'warning');
                return;
            }

            // 1. Récupérer le template complet
            const templateResponse = await API.getTemplate(templateId);
            if (!templateResponse || !templateResponse.success) {
                showNotification('❌ Erreur lors du chargement du modèle', 'error');
                return;
            }

            const template = templateResponse.data.template;
            const { rowIndex, cellIndex } = selectedCell;

            // 2. Insérer le template dans la cellule sélectionnée
            const updatedGrid = [...grid];
            updatedGrid[rowIndex].cells[cellIndex] = {
                ...updatedGrid[rowIndex].cells[cellIndex],
                elements: [], // Vider les éléments existants
                nestedGrid: template.grid_data,
                _templateMeta: {
                    templateId: template.id,
                    templateName: template.name,
                    version: 1,
                    insertedAt: new Date().toISOString(),
                    lastSyncedAt: new Date().toISOString(),
                    isLocked: false
                }
            };

            setGrid(updatedGrid);

            // 3. Si la signature est déjà sauvegardée, appeler l'API pour enregistrer la référence du template
            if (currentEntityId && !isTemplateMode) {
                try {
                    await API.insertTemplate(currentEntityId, {
                        template_id: templateId,
                        row_index: rowIndex,
                        cell_index: cellIndex
                    });
                    showNotification(`✅ Modèle "${template.name}" inséré dans la cellule sélectionnée!`, 'success');
                } catch (error) {
                    console.error('Insert template API error:', error);
                    // Ne pas bloquer l'insertion côté UI si l'API échoue
                    showNotification('⚠️ Modèle inséré (enregistrement en attente)', 'warning');
                }
            } else {
                showNotification(`✅ Modèle "${template.name}" inséré!`, 'success');
            }

            // Fermer la modal
            setShowTemplateModal(false);

        } catch (error) {
            console.error('Insert template error:', error);
            showNotification('❌ Erreur lors de l\'insertion du modèle', 'error');
        }
    };

    const handleDetachTemplate = (rowIndex, cellIndex) => {
        try {
            const cell = grid[rowIndex].cells[cellIndex];
            if (!cell._templateMeta) return;

            // Remove template metadata
            const updatedGrid = [...grid];
            delete updatedGrid[rowIndex].cells[cellIndex]._templateMeta;
            setGrid(updatedGrid);

            showNotification('✂️ Modèle détaché (converti en cellule normale)', 'info');
        } catch (error) {
            console.error('Detach template error:', error);
            showNotification('❌ Erreur lors du détachement', 'error');
        }
    };

    // Gestion de la planification de modifications
    const handleSchedule = () => {
        // Vérifier qu'une entité existe (pas de planification sur nouvelle entité non sauvegardée)
        if (!currentEntityId) {
            showNotification('⚠️ Veuillez d\'abord sauvegarder avant de planifier une modification', 'warning');
            return;
        }
        setShowScheduleModal(true);
    };

    const handleScheduleSubmit = async (scheduleData) => {
        try {
            await API.createScheduledChange(scheduleData);
            showNotification('✅ Modification planifiée avec succès', 'success');
            setShowScheduleModal(false);

            // Rafraîchir les compteurs de modifications planifiées
            if (refreshScheduledChangesRef) {
                refreshScheduledChangesRef();
            }
        } catch (error) {
            console.error('Schedule error:', error);
            showNotification('❌ Erreur lors de la planification: ' + error.message, 'error');
            throw error; // Re-throw pour que le modal puisse gérer l'erreur
        }
    };

    const handleViewScheduledChanges = () => {
        setShowScheduledChangesListModal(true);
    };

    return (
        <div className="app-container">
            <GlobalHeader />

            <div className="dashboard-header">
                <Link to={isTemplateMode ? "/templates" : "/signatures"} className="back-to-list">
                    <svg
                        width="20"
                        height="20"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                        />
                    </svg>
                    {isTemplateMode ? 'Retour aux modèles' : 'Retour aux signatures'}
                </Link>
            </div>

            <DashboardToolbar
                apiStatus={apiStatus}
                apiEnabled={API_CONFIG.enabled}
                onSave={handleSave}
                onClear={handleClear}
                grid={grid}
                signatureName={entityName}
                containerWidth={containerWidth}
                previewMode={previewMode}
                setPreviewMode={setPreviewMode}
                onRename={handleRename}
                onInsertTemplate={!isTemplateMode ? () => setShowTemplateModal(true) : undefined}
                isTemplateMode={isTemplateMode}
                onSchedule={handleSchedule}
                onViewScheduledChanges={handleViewScheduledChanges}
                hasEntity={!!currentEntityId}
                entityId={currentEntityId}
                onRefreshNeeded={setRefreshScheduledChangesRef}
                directoryMembers={directoryMembers}
                previewMember={previewMember}
                setPreviewMember={setPreviewMember}
            />

            <div className="main-content">
                <LeftSidebar
                    onDragStart={setDraggedItem}
                    previewMode={previewMode}
                    onInsertTemplate={!isTemplateMode ? () => setShowTemplateModal(true) : undefined}
                    selectedCell={selectedCell}
                />

                <Canvas
                    grid={grid}
                    setGrid={setGrid}
                    draggedItem={draggedItem}
                    setDraggedItem={setDraggedItem}
                    selectedElement={selectedElement}
                    setSelectedElement={setSelectedElement}
                    selectedCell={selectedCell}
                    setSelectedCell={setSelectedCell}
                    selectedRow={selectedRow}
                    setSelectedRow={setSelectedRow}
                    showNotification={showNotification}
                    containerWidth={containerWidth}
                    setContainerWidth={setContainerWidth}
                    previewMode={previewMode}
                    previewMember={previewMember}
                    onDetachTemplate={handleDetachTemplate}
                />

                <RightSidebar
                    selectedElement={selectedElement}
                    setSelectedElement={setSelectedElement}
                    selectedCell={selectedCell}
                    setSelectedCell={setSelectedCell}
                    grid={grid}
                    setGrid={setGrid}
                    apiEnabled={API_CONFIG.enabled && apiStatus === 'connected'}
                    onImageUpload={handleImageUpload}
                    showNotification={showNotification}
                    previewMode={previewMode}
                />
            </div>

            {notification && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    onClose={() => setNotification(null)}
                />
            )}

            {isTemplateMode ? (
                <TemplateSaveModal
                    isOpen={showNameModal}
                    title={currentEntityId ? "Modifier le modèle" : "Enregistrer le modèle"}
                    defaultName={entityName || 'Nouveau modèle'}
                    defaultDescription=""
                    onConfirm={handleTemplateModalConfirm}
                    onCancel={handleNameModalCancel}
                />
            ) : (
                <NameInputModal
                    isOpen={showNameModal}
                    title="Nom de la signature"
                    placeholder="Entrez un nom..."
                    defaultValue={entityName || 'Nouvelle signature'}
                    onConfirm={handleNameModalConfirm}
                    onCancel={handleNameModalCancel}
                />
            )}

            {!isTemplateMode && (
                <TemplateLibraryModal
                    isOpen={showTemplateModal}
                    onClose={() => setShowTemplateModal(false)}
                    onInsert={handleInsertTemplate}
                />
            )}

            {isTemplateMode && (
                <CascadeUpdateDialog
                    isOpen={showCascadeDialog}
                    usages={templateUsages}
                    onConfirm={handleCascadeConfirm}
                    onCancel={handleCascadeCancel}
                    loading={false}
                />
            )}

            {/* Modal de planification de modifications */}
            <ScheduleChangeModal
                isOpen={showScheduleModal}
                onClose={() => setShowScheduleModal(false)}
                onSchedule={handleScheduleSubmit}
                entityType={isTemplateMode ? 'template' : 'signature'}
                entityId={currentEntityId}
                entityName={entityName}
                currentGridData={grid}
            />

            {/* Modal de liste des modifications planifiées */}
            <ScheduledChangesListModal
                isOpen={showScheduledChangesListModal}
                onClose={() => setShowScheduledChangesListModal(false)}
                entityType={isTemplateMode ? 'template' : 'signature'}
                entityId={currentEntityId}
                onLoadVersion={(version) => {
                    // Charger la version dans l'éditeur
                    if (version.grid_data) {
                        setGrid(version.grid_data);
                    }
                    if (version.container_width) {
                        setContainerWidth(version.container_width);
                    }
                    showNotification(`Version #${version.version_number} chargée dans l'éditeur`, 'success');
                }}
            />
        </div>
    );
}

export default DashboardPage;
